
import { BlogPost } from '@/types/blog';

export const sampleBlogPosts: BlogPost[] = [
  {
    id: 1,
    title: "5 Mindfulness Techniques for Daily Stress Relief",
    slug: "5-mindfulness-techniques-daily-stress-relief",
    imageUrl: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "Mindfulness",
    date: "2024-12-15",
    author: "Dr. Sarah Johnson",
    summary: "Discover practical mindfulness techniques you can incorporate into your daily routine to manage stress and find inner peace.",
    content: `Stress has become an inevitable part of modern life, but it doesn't have to control you. Through mindfulness practices, you can develop the tools to manage stress effectively and cultivate a sense of calm in your daily routine.

Here are five powerful mindfulness techniques that you can start using today:

**1. The 4-7-8 Breathing Technique**
This simple yet effective breathing exercise can be done anywhere, anytime. Inhale for 4 counts, hold your breath for 7 counts, and exhale slowly for 8 counts. This technique activates your body's relaxation response and helps calm your nervous system.

**2. Body Scan Meditation**
Take 10-15 minutes to systematically focus on each part of your body, from your toes to the top of your head. Notice any tension or sensations without judgment, and consciously relax each area as you go.

**3. Mindful Walking**
Transform your daily walks into meditation sessions. Focus on the sensation of your feet touching the ground, the rhythm of your breath, and the sights and sounds around you. This practice grounds you in the present moment.

**4. The 5-4-3-2-1 Grounding Technique**
When feeling overwhelmed, identify 5 things you can see, 4 things you can touch, 3 things you can hear, 2 things you can smell, and 1 thing you can taste. This technique quickly brings your attention back to the present moment.

**5. Loving-Kindness Meditation**
Spend a few minutes sending positive thoughts and well-wishes to yourself, loved ones, and even difficult people in your life. This practice cultivates compassion and reduces stress-related negative emotions.

Remember, mindfulness is a practice. Start with just 5-10 minutes a day and gradually increase as you become more comfortable with these techniques.`
  },
  {
    id: 2,
    title: "Understanding Your Inner Voice: A Journey to Self-Compassion",
    slug: "understanding-inner-voice-journey-self-compassion",
    imageUrl: "https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "Self-Awareness",
    date: "2024-12-10",
    author: "Dr. Michael Chen",
    summary: "Learn to recognize and transform your inner critic into a compassionate inner voice that supports your growth and well-being.",
    content: `Your inner voice – that constant stream of thoughts and commentary running through your mind – has tremendous power over how you feel about yourself and navigate the world. For many people, this voice tends to be critical, harsh, and unforgiving. But it doesn't have to be this way.

**Recognizing Your Inner Critic**

The first step in developing self-compassion is becoming aware of your inner dialogue. Notice when your thoughts become self-critical or judgmental. Common phrases from the inner critic include:
- "I'm not good enough"
- "I always mess things up"
- "Everyone else is better than me"
- "I should be doing more"

**The Science of Self-Compassion**

Research by Dr. Kristin Neff shows that self-compassion has three key components:
1. **Self-kindness**: Treating yourself with the same kindness you'd show a good friend
2. **Common humanity**: Recognizing that struggle and imperfection are part of the human experience
3. **Mindfulness**: Observing your thoughts and feelings without judgment

**Transforming Your Inner Voice**

Here are practical strategies to cultivate a more compassionate inner voice:

**Practice Self-Talk Reframing**
When you notice harsh self-criticism, pause and ask: "What would I say to a friend in this situation?" Then extend that same kindness to yourself.

**Use Your Name**
Research shows that talking to yourself using your name (rather than "I") creates psychological distance and promotes clearer thinking.

**Develop a Self-Compassion Phrase**
Create a go-to phrase for difficult moments, such as: "This is a moment of struggle. Struggle is part of life. May I be kind to myself in this moment."

**The Compassionate Friend Technique**
Imagine a wise, compassionate friend who knows all your struggles and still loves you unconditionally. What would they say to you right now?

Remember, developing self-compassion is a practice that takes time. Be patient with yourself as you learn to speak to yourself with more kindness and understanding.`
  },
  {
    id: 3,
    title: "Building Resilience Through Mindful Living",
    slug: "building-resilience-mindful-living",
    imageUrl: "https://images.unsplash.com/photo-1470813740244-df37b8c1edcb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "Personal Growth",
    date: "2024-12-05",
    author: "Dr. Aisha Patel",
    summary: "Explore how mindful living practices can strengthen your resilience and help you bounce back from life's challenges with greater ease.",
    content: `Resilience – the ability to bounce back from adversity, adapt to change, and grow stronger through challenges – is not a fixed trait. It's a skill that can be developed through mindful living practices.

**What is Mindful Living?**

Mindful living means bringing conscious awareness to your daily experiences, thoughts, and emotions. It's about being present in each moment rather than operating on autopilot.

**The Resilience-Mindfulness Connection**

Mindfulness builds resilience by:
- Increasing emotional regulation
- Reducing reactivity to stress
- Enhancing self-awareness
- Promoting adaptive coping strategies
- Building mental flexibility

**Practical Strategies for Mindful Living**

**1. Morning Intention Setting**
Start each day by setting a clear intention for how you want to show up in the world. This creates a sense of purpose and direction.

**2. Mindful Transitions**
Use transitions between activities as opportunities for brief mindfulness practices. Take three conscious breaths before starting a new task.

**3. Emotional Awareness Check-ins**
Throughout the day, pause and ask yourself: "How am I feeling right now?" Notice emotions without immediately trying to change them.

**4. Gratitude Practice**
End each day by reflecting on three things you're grateful for. This shifts your focus toward positive aspects of your experience.

**5. Mindful Response vs. Reaction**
When faced with challenges, pause before responding. This small space allows you to choose a thoughtful response rather than an automatic reaction.

**Building Your Resilience Toolkit**

Create a personalized toolkit of practices that work for you:
- Breathing exercises for immediate stress relief
- Movement practices like yoga or walking
- Journaling for processing emotions
- Connection with supportive people
- Engagement in meaningful activities

**The Long-term Benefits**

Regular mindful living practices lead to:
- Greater emotional stability
- Improved problem-solving abilities
- Enhanced relationships
- Increased life satisfaction
- Better physical health

Remember, building resilience is a journey, not a destination. Each mindful moment contributes to your overall capacity to thrive in the face of life's inevitable challenges.`
  },
  {
    id: 4,
    title: "The Science Behind Mindfulness and Mental Health",
    slug: "science-mindfulness-mental-health",
    imageUrl: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "Mental Health",
    date: "2024-11-30",
    author: "Dr. Sarah Johnson",
    summary: "Discover the fascinating research behind how mindfulness practices create measurable changes in the brain and improve mental health outcomes.",
    content: `The ancient practice of mindfulness has gained significant attention in modern neuroscience and psychology. Research reveals that mindfulness creates measurable changes in brain structure and function, leading to improved mental health outcomes.

**Neuroplasticity and Mindfulness**

The brain's ability to reorganize and form new neural connections throughout life is called neuroplasticity. Mindfulness practices actively promote positive neuroplastic changes by:
- Strengthening areas associated with attention and emotional regulation
- Reducing activity in the default mode network (associated with rumination)
- Increasing gray matter density in key brain regions

**Key Brain Changes from Mindfulness Practice**

**1. Prefrontal Cortex Enhancement**
Regular mindfulness practice strengthens the prefrontal cortex, the brain's "CEO" responsible for:
- Executive decision-making
- Emotional regulation
- Attention control
- Self-awareness

**2. Amygdala Reduction**
The amygdala, our brain's alarm system, becomes less reactive with mindfulness practice, leading to:
- Reduced anxiety and stress responses
- Better emotional regulation
- Decreased fear-based reactions

**3. Hippocampus Growth**
The hippocampus, crucial for learning and memory, shows increased gray matter density, resulting in:
- Improved memory formation
- Better learning capacity
- Enhanced cognitive flexibility

**Clinical Applications**

Research supports mindfulness-based interventions for various mental health conditions:

**Depression**: Mindfulness-Based Cognitive Therapy (MBCT) reduces relapse rates by 40-50%
**Anxiety**: Regular practice significantly reduces anxiety symptoms and worry
**PTSD**: Mindfulness helps process trauma and reduce hypervigilance
**Addiction**: Mindfulness-based relapse prevention shows promising results

**The Default Mode Network**

One of the most significant discoveries is mindfulness's effect on the default mode network (DMN) – brain regions active during rest and self-referential thinking. Overactivity in the DMN is linked to:
- Depression
- Anxiety
- Rumination
- Self-criticism

Mindfulness practice helps regulate DMN activity, reducing these mental health challenges.

**Physiological Benefits**

Beyond brain changes, mindfulness affects the entire body:
- Reduced cortisol (stress hormone) levels
- Improved immune function
- Lower blood pressure
- Better sleep quality
- Reduced inflammation markers

**Evidence-Based Practices**

Research-supported mindfulness interventions include:
- Mindfulness-Based Stress Reduction (MBSR)
- Mindfulness-Based Cognitive Therapy (MBCT)
- Acceptance and Commitment Therapy (ACT)
- Dialectical Behavior Therapy (DBT)

**Getting Started**

The good news is that benefits can be seen with just 8 weeks of regular practice:
- Start with 10-15 minutes daily
- Use guided meditations initially
- Focus on consistency over duration
- Be patient with the process

The science is clear: mindfulness is not just a feel-good practice but a powerful tool for transforming both brain and mind, leading to lasting improvements in mental health and well-being.`
  },
  {
    id: 5,
    title: "Creating Sacred Spaces in Your Daily Routine",
    slug: "creating-sacred-spaces-daily-routine",
    imageUrl: "https://images.unsplash.com/photo-1500375592092-40eb2168fd21?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "Lifestyle",
    date: "2024-11-25",
    author: "Dr. Michael Chen",
    summary: "Learn how to integrate mindful moments and sacred spaces into your busy schedule to maintain balance and inner peace throughout the day.",
    content: `In our fast-paced world, finding moments of peace and reflection can seem impossible. However, creating sacred spaces – both physical and temporal – in your daily routine can transform your relationship with stress and help you maintain balance throughout even the busiest days.

**What Are Sacred Spaces?**

Sacred spaces aren't necessarily religious or spiritual locations. They are intentionally created environments or moments that allow you to:
- Connect with your inner self
- Practice mindfulness
- Recharge your mental and emotional energy
- Find peace amidst chaos

**Physical Sacred Spaces**

**1. Your Meditation Corner**
Designate a specific area in your home for mindfulness practice:
- Choose a quiet corner with minimal distractions
- Add elements that promote calm (plants, soft lighting, comfortable cushions)
- Keep it simple and clutter-free
- Use this space only for mindful activities

**2. Nature Sanctuaries**
Connect with outdoor spaces regularly:
- Find a nearby park, garden, or natural area
- Create a small garden or tend to houseplants
- Sit by a window with a view of trees or sky
- Take mindful walks in nature

**3. Bathroom Retreats**
Transform routine activities into mindful moments:
- Practice deep breathing while brushing teeth
- Use shower time for body awareness meditation
- Apply lotion mindfully, appreciating your body
- Look in the mirror with kindness and gratitude

**Temporal Sacred Spaces**

**1. Morning Rituals**
Start your day with intention:
- Wake up 10 minutes earlier for quiet time
- Practice gratitude before checking your phone
- Set daily intentions while drinking morning coffee
- Do gentle stretches or breathing exercises

**2. Transition Moments**
Use natural breaks as mini-retreats:
- Take three conscious breaths before entering meetings
- Practice mindful walking between activities
- Use commute time for meditation or reflection
- Create brief pauses between tasks

**3. Evening Wind-Down**
End your day mindfully:
- Reflect on positive moments from the day
- Practice gentle yoga or stretching
- Read something inspiring
- Journal about your experiences and feelings

**Micro-Moments of Sacredness**

You don't need hours to create sacred space. These micro-practices take just 1-3 minutes:

**The Mindful Minute**
Set random phone alarms throughout the day. When they ring:
- Stop what you're doing
- Take three deep breaths
- Notice your surroundings
- Return to activities with renewed awareness

**Sacred Pauses**
Before eating, pause to:
- Appreciate your food
- Feel gratitude for nourishment
- Eat the first few bites mindfully
- Notice flavors, textures, and sensations

**Mindful Transitions**
When moving between locations:
- Walk more slowly and deliberately
- Notice your breath and body
- Observe your environment
- Use doorways as reminders to return to presence

**Creating Sacred Space at Work**

**Desk Sanctuaries**
- Keep a small plant or meaningful object visible
- Use a special mug for mindful tea or coffee breaks
- Create visual reminders of your values or goals
- Take brief breathing breaks between tasks

**Meeting Mindfulness**
- Arrive a few minutes early to center yourself
- Practice active listening during conversations
- Take notes mindfully, staying present
- Use bathroom breaks for quick mindfulness check-ins

**Digital Sacred Spaces**

**Mindful Technology Use**
- Create phone-free zones in your home
- Use apps for guided meditations
- Set mindful wallpapers or reminders
- Practice digital detox periods

**The Ripple Effect**

Creating sacred spaces in your routine has far-reaching benefits:
- Reduced stress and anxiety
- Improved focus and creativity
- Better relationships and communication
- Enhanced emotional regulation
- Greater life satisfaction

**Starting Small**

Begin with just one sacred space or moment per day:
- Choose something that feels natural and appealing
- Be consistent for at least one week
- Notice how this practice affects your overall well-being
- Gradually add more sacred moments as they become habit

Remember, sacred spaces are not about perfection or elaborate rituals. They're about creating intentional moments of presence and peace within the rhythm of your daily life. Even the busiest schedule can accommodate these precious moments of sanctuary.`
  }
];

// Blog settings for featured posts and categories
export const blogSettings = {
  featuredPosts: [1, 2, 3], // IDs of posts to feature
  categoriesOrder: ['Mindfulness', 'Self-Awareness', 'Personal Growth', 'Mental Health', 'Lifestyle']
};
